import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Store, Users, Copy, Wand2, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const MATERIAS = ["Todas", "Civil", "Penal", "Laboral", "Mercantil", "Administrativo", "Familia", "Fiscal", "Otro"];

export default function Marketplace() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [materia, setMateria] = useState("Todas");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (materia && materia !== "Todas") params.set("materia", materia);
      const { data } = await api.get(`/templates/marketplace?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [materia]);

  const duplicate = async (t) => {
    try {
      const { data } = await api.post(`/templates/${t.id}/duplicate`);
      toast.success(`"${data.name}" duplicada en tus plantillas`);
      nav("/plantillas");
    } catch (e) { toast.error(apiError(e)); }
  };

  const goToGenerator = (t) => {
    nav(`/generador?template=${t.id}`);
  };

  const canFork = user?.role === "admin" || user?.role === "abogado";

  return (
    <PageContainer>
      <PageHeader
        title="Marketplace de Plantillas"
        subtitle="Plantillas compartidas por el despacho, ordenadas por uso. Duplícalas para personalizarlas o úsalas directamente en el Generador."
      />

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar por nombre, descripción o etiqueta..."
            className="rounded-sm pl-9"
            data-testid="marketplace-search-input"
          />
        </div>
        <Select value={materia} onValueChange={setMateria}>
          <SelectTrigger className="rounded-sm w-48" data-testid="marketplace-materia-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MATERIAS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={load} className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]" data-testid="marketplace-search-button">
          Buscar
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E5E7EB] bg-white">
          <Store className="w-10 h-10 mx-auto text-neutral-400" />
          <p className="mt-4 text-sm text-neutral-500">
            No hay plantillas compartidas todavía. Activa el interruptor <b>Compartir en Marketplace</b> al crear una plantilla.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-[#E5E7EB] p-5 flex flex-col hover:shadow-sm transition-shadow" data-testid={`marketplace-card-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-widest text-[#7B1826]">{t.materia || "Sin materia"}</span>
                    <span className="text-neutral-300">·</span>
                    <span className="font-mono text-[10px] text-neutral-500">v{t.version || 1}</span>
                  </div>
                  <h3 className="font-display text-xl mt-0.5 text-[#0F172A]">{t.name}</h3>
                  {t.category && <div className="text-xs text-neutral-500 mt-0.5">{t.category}</div>}
                </div>
              </div>
              {t.description && <p className="text-sm text-neutral-600 line-clamp-3">{t.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(t.tags || []).slice(0, 4).map((tag) => (
                  <Badge key={tag} variant="outline" className="rounded-sm text-[10px] border-[#E5E7EB]">{tag}</Badge>
                ))}
              </div>
              <div className="flex-1" />
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F3F4F6] text-[11px] text-neutral-500">
                <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{t.usage_count || 0} usos</span>
                <span className="truncate max-w-[140px]">{t.created_by_name || "—"}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => goToGenerator(t)}
                  size="sm"
                  className="rounded-sm flex-1 bg-[#7B1826] hover:bg-[#5D121D]"
                  data-testid={`marketplace-use-${t.id}`}
                >
                  <Wand2 className="w-3 h-3 mr-1.5" /> Usar
                </Button>
                {canFork && (
                  <Button
                    onClick={() => duplicate(t)}
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    data-testid={`marketplace-duplicate-${t.id}`}
                  >
                    <Copy className="w-3 h-3 mr-1.5" /> Duplicar
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
